import { useCallback, useEffect, useMemo, useState } from "react";
import { eventBus } from "./eventBus";
import type { ToothEvent } from "../../../types/domain";
import type { ToothEventPayload } from "../../../types/contracts";

/**
 * Simple in-memory undo/redo stack for ToothEvent streams.
 * Does NOT delete original events; instead emits compensating events.
 */

/** Event bus may emit camelCase (type, encounterId); stack stores ToothEvent-like items. */
type ToothEventLike = ToothEvent & { type?: string; encounterId?: number };

function invertEvent(event: ToothEventLike | null): ToothEventLike | null {
  if (!event) return null;

  switch (event.type) {
    case "mark":
      return { ...event, type: "unmark", payload: { ...(event.payload ?? {}), undo: true } as ToothEventPayload };
    case "unmark":
      return { ...event, type: "mark", payload: { ...(event.payload ?? {}), undo: true } as ToothEventPayload };
    case "procedure":
      return {
        ...event,
        type: "procedure",
        payload: { ...(event.payload ?? {}), undo: true } as ToothEventPayload,
      };
    default:
      return null; // non-invertible
  }
}

export function useUndoRedo(currentEncounterId: number | null | undefined) {
  const [stack, setStack] = useState<{ past: ToothEventLike[]; future: ToothEventLike[] }>({ past: [], future: [] });
  const storageKey = useMemo(
    () => (currentEncounterId ? `tooth_undo_stack_${currentEncounterId}` : null),
    [currentEncounterId]
  );

  // Listen to all TOOTH_EVENTs to build the "past" list for this encounter
  useEffect(() => {
    const handler = (payload: unknown) => {
      const event = payload as ToothEventLike;
      if (!event) return;
      const encounterId = (event.encounterId ?? (event as { encounter_id?: number }).encounter_id) as number | undefined;
      if (currentEncounterId && encounterId !== currentEncounterId) return;
      const p = (event.payload || {}) as { undo?: boolean; redo?: boolean; replay?: boolean };
      if (p.undo || p.redo || p.replay) return;

      setStack((prev) => ({
        past: [...prev.past, event],
        future: [], // any new event invalidates redo buffer
      }));
    };

    eventBus.on("TOOTH_EVENT", handler);
    return () => {
      eventBus.off("TOOTH_EVENT", handler);
    };
  }, [currentEncounterId]);

  // Reset stack when encounter changes
  useEffect(() => {
    setStack({ past: [], future: [] });
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { past?: ToothEventLike[]; future?: ToothEventLike[] };
        setStack({
          past: Array.isArray(parsed?.past) ? parsed.past : [],
          future: Array.isArray(parsed?.future) ? parsed.future : [],
        });
      }
    } catch (e) {
      console.warn("useUndoRedo: Failed to load from localStorage", e);
    }
  }, [currentEncounterId, storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const trimmed = {
        past: stack.past.slice(-100),
        future: stack.future.slice(-100),
      };
      localStorage.setItem(storageKey, JSON.stringify(trimmed));
    } catch (e) {
      console.warn("useUndoRedo: Failed to save to localStorage", e);
    }
  }, [stack, storageKey]);

  const undo = useCallback(() => {
    setStack((prev) => {
      if (!prev.past.length) return prev;
      const last: ToothEventLike = prev.past[prev.past.length - 1];
      const inverse = invertEvent(last);
      if (inverse) {
        eventBus.emit("TOOTH_EVENT", inverse);
      }
      return {
        past: prev.past.slice(0, -1),
        future: [last, ...prev.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setStack((prev) => {
      if (!prev.future.length) return prev;
      const next: ToothEventLike = prev.future[0];
      eventBus.emit("TOOTH_EVENT", {
        ...next,
        payload: { ...(next.payload ?? {}), redo: true } as ToothEventPayload,
      });
      return {
        past: [...prev.past, next],
        future: prev.future.slice(1),
      };
    });
  }, []);

  return {
    canUndo: stack.past.length > 0,
    canRedo: stack.future.length > 0,
    undo,
    redo,
  };
}
